import React from 'react';
import { Box, Typography } from '@mui/material';
import RelatedSectionShell, { TilesGrid } from './RelatedSectionShell';
import { slugify } from '../../utils/slug';
import ProductBadgeTile from './tiles/ProductBadgeTile';
import SiblingProductsTable from './tables/SiblingProductsTable';

/**
 * Build sibling products:
 * 1) Find set of jobs where THIS product is the chosen solution.
 * 2) Among ALL interviews that include ANY of those jobs, group by root-level solution_choice_category
 *    EXCLUDING the current product.
 * 3) Rank by occurrences (per-interview de-dup), compute share over those interviews, and avg satisfaction_level.
 * 4) Keep instances (sub-rows) with basic interview info and the specific job that matched.
 */
function buildSiblings(stories = [], productName) {
  const target = slugify(productName || '');
  if (!productName) return { rows: [], top3: [], sectionTotal: 0 };

  // 1) Interviews where THIS product is chosen → collect jobs
  const matches = stories.filter(
    (s) => s?.solution_choice_category && slugify(s.solution_choice_category) === target
  );
  const jobSet = new Set();
  matches.forEach((s) => {
    const jobs = Array.isArray(s?.jobs_to_be_done) ? s.jobs_to_be_done : [];
    jobs.forEach((j) => {
      const name = j?.job_to_be_done_category;
      if (name) jobSet.add(slugify(name));
    });
  });

  if (jobSet.size === 0) return { rows: [], top3: [], sectionTotal: 0 };

  // 2) Among ALL stories that include ANY of those jobs, aggregate other chosen solutions
  const cohort = stories.filter((s) => {
    const jobs = Array.isArray(s?.jobs_to_be_done) ? s.jobs_to_be_done : [];
    return jobs.some((j) => jobSet.has(slugify(j?.job_to_be_done_category || '')));
  });
  const sectionTotal = cohort.length;

  const map = new Map(); // productSlug -> { name, count, sumSat, nSat, instances: [] }
  cohort.forEach((s) => {
    const prod = s?.solution_choice_category;
    if (!prod) return;
    const slug = slugify(prod);
    if (slug === target) return; // exclude current product

    // Find which job(s) in this interview intersect our jobSet for context in sub-rows
    const jobs = Array.isArray(s?.jobs_to_be_done) ? s.jobs_to_be_done : [];
    const matchedJobs = jobs
      .filter((j) => jobSet.has(slugify(j?.job_to_be_done_category || '')))
      .map((j) => j?.job_to_be_done_category)
      .filter(Boolean);

    if (!map.has(slug)) {
      map.set(slug, { name: prod, count: 0, sumSat: 0, nSat: 0, instances: [] });
    }
    const g = map.get(slug);
    g.count += 1;
    const sat = Number(s?.satisfaction_level);
    if (Number.isFinite(sat)) {
      g.sumSat += sat;
      g.nSat += 1;
    }

    g.instances.push({
      name: s?.customer_name || 'Interview',
      date: s?.conversation_date || '',
      segmentation: s?.segmentation_data || {},
      job: matchedJobs[0] || '', // show first matched job for context
      chosenSolution: prod,
      satisfaction: Number.isFinite(sat) ? sat : null,
      summary: s?.satisfaction_level_justification || '',
      href: `/conversations?search=${encodeURIComponent(s?.customer_name || '')}`,
    });
  });

  const rows = [...map.values()].map((g) => {
    const avgSat = g.nSat ? g.sumSat / g.nSat : null;
    const pct = sectionTotal ? Math.round((g.count / sectionTotal) * 100) : 0;
    return {
      name: g.name,
      count: g.count,
      pct,
      avgSatisfaction: avgSat,
      href: `/product/${slugify(g.name)}`,
      instances: g.instances.sort((a, b) => {
        const ta = Date.parse(a.date || '') || 0;
        const tb = Date.parse(b.date || '') || 0;
        return tb - ta;
      }),
    };
  });

  rows.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const top3 = rows.slice(0, 3).map((r) => ({
    title: r.name,
    count: r.count,
    pct: r.pct,
    avgSatisfaction: r.avgSatisfaction,
    href: r.href,
  }));

  return { rows, top3, sectionTotal };
}

export default function RelatedSiblingProducts({ productName, customerStories = [] }) {
  const memo = React.useMemo(
    () => buildSiblings(customerStories, productName),
    [customerStories, productName]
  );

  if (!memo.rows.length) return null;

  const section = {
    title: 'Other solutions used for these Jobs',
    description: `Among interviews that mention the same Jobs as “${productName}”.`,
    section_total_interviews: memo.sectionTotal,
  };

  return (
    <RelatedSectionShell section={section}>
      {/* Top-3 sibling products */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Top solutions (by occurrences)
        </Typography>
        <TilesGrid
          tiles={memo.top3}
          renderTile={(t) => (
            <ProductBadgeTile
              title={t.title}
              count={t.count}
              pct={t.pct}
              avgSatisfaction={t.avgSatisfaction}
              href={t.href}
            />
          )}
        />
      </Box>

      {/* Full expandable table */}
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          All solutions for these Jobs
        </Typography>
        <SiblingProductsTable rows={memo.rows} />
      </Box>
    </RelatedSectionShell>
  );
}
